import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Settings, X, User, Lock, Palette } from 'lucide-react';
import UserProfileTab from './UserProfileTab';
import UserSecurityTab from './UserSecurityTab';
import UserPreferencesTab from './UserPreferencesTab';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'profile' | 'security' | 'preferences';

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  const tabs = [
    { id: 'profile', label: 'Perfil', icon: User },
    { id: 'security', label: 'Segurança', icon: Lock },
    { id: 'preferences', label: 'Preferências', icon: Palette }
  ] as const;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card variant="glass" className="w-full max-w-4xl max-h-[90vh] bg-background-secondary border border-white/10 overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-white/10">
          <div>
            <CardTitle className="text-xl font-heading text-foreground-primary flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurações do Usuário
            </CardTitle>
            <p className="text-foreground-tertiary mt-1">
              Gerencie suas informações pessoais e preferências
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 border-white/20 hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        {/* Tabs Navigation */}
        <div className="flex border-b border-white/10">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`px-6 py-4 text-sm font-medium relative flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'text-blue-400 bg-blue-500/10'
                    : 'text-foreground-secondary hover:text-foreground-primary hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <CardContent className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground-primary">Informações Pessoais</h3>
                  <p className="text-sm text-foreground-tertiary">
                    Atualize suas informações pessoais e dados de contato
                  </p>
                </div>
                
                <UserProfileTab />
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground-primary">Segurança</h3>
                  <p className="text-sm text-foreground-tertiary">
                    Gerencie sua senha e configurações de segurança
                  </p>
                </div>
                
                <UserSecurityTab />
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground-primary">Preferências</h3>
                  <p className="text-sm text-foreground-tertiary">
                    Personalize sua experiência com tema, idioma e outras preferências
                  </p>
                </div>
                
                <UserPreferencesTab />
              </div>
            )}
          </CardContent>
        </div>
      </Card>
    </div>
  );
};

export default UserSettingsModal;